import { hashTypedData, recoverTypedDataAddress } from "viem";
import type { Address, Hex, TypedDataDomain } from "viem";

/** The Intent of one batch (D64, D67): a signed message, not storage. No fund field. */
export interface Intent {
  client: Address;
  identity: Address;
  token: Address;
  payTo: Address;
  max: bigint;
  nonce: Hex;
  context: Hex;
}

/** The wire form of an Intent: bigint as a decimal string. */
export interface IntentJson {
  client: Address;
  identity: Address;
  token: Address;
  payTo: Address;
  max: string;
  nonce: Hex;
  context: Hex;
}

export const INTENT_TYPES = {
  Intent: [
    { name: "client", type: "address" },
    { name: "identity", type: "address" },
    { name: "token", type: "address" },
    { name: "payTo", type: "address" },
    { name: "max", type: "uint256" },
    { name: "nonce", type: "bytes32" },
    { name: "context", type: "bytes32" },
  ],
} as const;

export function intentDomain(chainId: number, verifyingContract: Address): TypedDataDomain {
  return { name: "ServiceAuthorization", version: "2", chainId, verifyingContract };
}

export function intentToJson(i: Intent): IntentJson {
  return { ...i, max: i.max.toString() };
}

export function intentFromJson(j: IntentJson): Intent {
  return { ...j, max: BigInt(j.max) };
}

export function intentDigest(intent: Intent, chainId: number, verifyingContract: Address): Hex {
  return hashTypedData({
    domain: intentDomain(chainId, verifyingContract),
    types: INTENT_TYPES,
    primaryType: "Intent",
    message: intent,
  });
}

export interface TypedDataSigner {
  address: Address;
  signTypedData(args: {
    domain: TypedDataDomain;
    types: typeof INTENT_TYPES;
    primaryType: "Intent";
    message: Intent;
  }): Promise<Hex>;
}

/** The DO signs the Intent with the conversation's agent key (D64). */
export async function signIntent(
  agent: TypedDataSigner,
  intent: Intent,
  chainId: number,
  verifyingContract: Address,
): Promise<Hex> {
  return agent.signTypedData({
    domain: intentDomain(chainId, verifyingContract),
    types: INTENT_TYPES,
    primaryType: "Intent",
    message: intent,
  });
}

export async function recoverIntentSigner(
  intent: Intent,
  signature: Hex,
  chainId: number,
  verifyingContract: Address,
): Promise<Address | null> {
  try {
    return await recoverTypedDataAddress({
      domain: intentDomain(chainId, verifyingContract),
      types: INTENT_TYPES,
      primaryType: "Intent",
      message: intent,
      signature,
    });
  } catch {
    return null;
  }
}
